import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABand from "../components/CTABand";
import PageMeta from "../components/PageMeta";
import { PROJECTS } from "../data/content";

const Projects = () => {
  const featured = PROJECTS;

  return (
    <main data-testid="projects-page">
      <PageMeta title="Hospital Projects | AccuSurge Medical Solutions" description="Case studies of healthcare facility projects coordinated end-to-end by AccuSurge — Upasana (OPD, 8-bed dialysis unit, ongoing 25-bed hospital) and Seba Nursing Home (30-bed facility with 5-bed ICU)." />
      <section className="bg-brand-950 pb-20 pt-40 md:pb-28 grain relative overflow-hidden">
        <div className="absolute -right-20 top-10 h-80 w-80 rounded-full bg-accent/15 blur-[110px]" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <span className="eyebrow text-accent">Projects</span>
            <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white text-balance">
              From Healthcare Vision to Operational Facility.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
              AccuSurge supports healthcare institutions in setting up and upgrading facilities by coordinating
              equipment, furniture, infrastructure requirements and project execution support.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-x">
          <Stagger className="grid grid-cols-1 gap-8">
            {featured.map((p) => (
              <StaggerItem key={p.id}>
                <article className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white lg:grid-cols-[1fr_1.1fr]" data-testid={`case-study-${p.slug}`}>
                  <div className="relative h-64 overflow-hidden lg:h-auto">
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                    <span className="absolute left-4 top-4 rounded-full bg-brand-600/95 px-3 py-1 text-[11px] font-semibold text-white" data-testid={`project-status-${p.slug}`}>{p.status}</span>
                  </div>
                  <div className="p-8 md:p-12">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-600">{p.facility_type}</span>
                    <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold tracking-tight text-ink">{p.name}</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-slate-600">{p.description}</p>
                    <dl className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {[
                        ["Client / Facility", p.client],
                        ["Location", p.location],
                        ["Project Scope", p.scope],
                        ["Capacity", p.capacity],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{k}</dt>
                          <dd className="mt-1 text-sm text-slate-700">{v}</dd>
                        </div>
                      ))}
                    </dl>
                    {p.services?.length > 0 && (
                      <div className="mt-6 flex flex-wrap gap-2">
                        {p.services.map((s) => <span key={s} className="chip !bg-brand-50 !border-brand-100 !text-brand-700">{s}</span>)}
                      </div>
                    )}
                    <Link to={`/projects/${p.slug}`} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-accent-600" data-testid={`case-study-link-${p.slug}`}>
                      Read the case study <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-slate-100 py-16" data-testid="trusted-clients">
        <div className="container-x">
          <Reveal>
            <p className="text-center font-mono text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Trusted by Healthcare Institutions
            </p>
            <p className="mt-2 text-center text-xs text-slate-400">Facilities where AccuSurge has delivered end-to-end project support.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {["Upasana", "Seba Nursing Home"].map((c) => (
                <span key={c} className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 font-heading text-base font-semibold text-brand-800" data-testid={`client-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
};

export default Projects;
